import { useQueryClient } from "@tanstack/react-query";
import Pusher, { Channel } from "pusher-js";
import { useEffect, useRef } from "react";

import { getReportsByOrderId, useSessionUser } from "@web-app/api";
import config from "@web-app/config";
import { Report } from "@web-app/types";

export function useManagePageEvents(
  options: {
    enabled?: boolean;
  } = {},
) {
  const { enabled = true } = options;
  const { data: user } = useSessionUser();
  const queryClient = useQueryClient();
  const pusherRef = useRef<Pusher | null>(null);
  const channelRef = useRef<Channel | null>(null);

  useEffect(() => {
    if (!user || !enabled) {
      return;
    }

    if (pusherRef.current) {
      return;
    }

    const pusher = new Pusher(config.SOKETI_APP_KEY, {
      cluster: "",
      enabledTransports: ["ws", "wss"],
      forceTLS: false,
      wsHost: config.SOKETI_HOST,
      wsPort: parseInt(config.SOKETI_PORT),
      wssPort: parseInt(config.SOKETI_PORT),
    });

    pusher.connection.bind("error", () => {
      console.error("Soketi - Something went wrong!");
    });

    pusher.connection.bind("connected", () => {
      console.info("Soketi - Connected!");
    });

    pusherRef.current = pusher;

    return () => {
      if (pusherRef.current) {
        pusherRef.current.disconnect();
        pusherRef.current = null;
      }
    };
  }, [user, enabled]);

  useEffect(() => {
    if (!user || !pusherRef.current || !enabled) {
      return;
    }

    const pusher = pusherRef.current;
    const workspaceChannelName = "internal-api@" + user.currentWorkspaceId;
    const workspaceChannel = pusher.subscribe(workspaceChannelName);

    channelRef.current = workspaceChannel;

    workspaceChannel.bind(
      "reportCreated",
      async (data: {
        createdAt: string;
        orderId: string;
        reportId: string;
        type: string;
      }) => {
        queryClient.invalidateQueries({
          queryKey: ["order-reports", data.orderId],
        });

        const batchQueries = queryClient
          .getQueryCache()
          .findAll({ queryKey: ["batch-reports"] });

        for (const query of batchQueries) {
          const queryKey = query.queryKey as string[];
          if (queryKey.includes(data.orderId)) {
            try {
              const updatedReports = await getReportsByOrderId(data.orderId);

              queryClient.setQueryData(
                queryKey,
                (oldData: Record<string, Report[]>) => {
                  if (oldData) {
                    return {
                      ...oldData,
                      [data.orderId]: updatedReports,
                    };
                  }
                  return oldData;
                },
              );
            } catch {
              queryClient.invalidateQueries({ queryKey });
            }
          }
        }
      },
    );

    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }
    };
  }, [queryClient, user, enabled]);

  return {
    isConnected: pusherRef.current?.connection?.state === "connected",
  };
}
